import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  GetUserCommand,
  GlobalSignOutCommand,
  AuthFlowType
} from '@aws-sdk/client-cognito-identity-provider';

// Check if we're in development mode with mock auth
const isDevelopmentMode = import.meta.env.VITE_ENVIRONMENT === 'development';
const useMockAuth = isDevelopmentMode && (
  import.meta.env.VITE_USER_POOL_ID === 'us-east-1_mockpoolid' ||
  import.meta.env.VITE_DISABLE_AUTH === 'true'
);

// AWS Cognito client configuration
const cognitoClient = new CognitoIdentityProviderClient({
  region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
});

const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID || '';

// Mock users for development
const mockUsers = [
  {
    id: 'mock-admin-1',
    email: 'admin@ibusiness.ai',
    password: 'admin123',
    firstName: 'Admin',
    lastName: 'User',
    role: 'admin' as const,
    department: 'Administration',
    isActive: true,
  },
  {
    id: 'mock-manager-1',
    email: 'manager@ibusiness.ai', 
    password: 'manager123',
    firstName: 'Manager',
    lastName: 'User',
    role: 'manager' as const,
    department: 'Operations',
    isActive: true,
  },
  {
    id: 'mock-analyst-1',
    email: 'analyst@ibusiness.ai',
    password: 'analyst123', 
    firstName: 'Analyst',
    lastName: 'User',
    role: 'analyst' as const,
    department: 'Analytics',
    isActive: true,
  },
  {
    id: 'mock-viewer-1',
    email: 'viewer@ibusiness.ai',
    password: 'viewer123',
    firstName: 'Viewer',
    lastName: 'User', 
    role: 'viewer' as const,
    department: 'Operations',
    isActive: true,
  },
];

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'analyst' | 'viewer' | 'client';
  department?: string;
  clientId?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  error: string | null;
}

export interface SignUpAttributes {
  email?: string;
  given_name?: string;
  family_name?: string;
  [key: string]: string | undefined;
}

export interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, attributes: SignUpAttributes) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    token: null,
    error: null,
  });

  // Check if user is already authenticated on app load
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      if (useMockAuth) {
        // Check for stored mock user in localStorage
        const storedUser = localStorage.getItem('mockAuthUser');
        
        if (storedUser) {
          const user = JSON.parse(storedUser);
          // Create the enhanced mock token with user data
          const mockToken = `mock-jwt-token:${btoa(JSON.stringify(user))}`;
          localStorage.setItem('authToken', mockToken);
          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user,
            token: mockToken,
            error: null,
          });
          return;
        }
      } else {
        // Check for stored access token
        const accessToken = localStorage.getItem('authToken');
        
        if (accessToken && !accessToken.startsWith('mock-jwt-token')) {
          try {
            const getUserCommand = new GetUserCommand({
              AccessToken: accessToken,
            });
            
            const currentUser = await cognitoClient.send(getUserCommand);
            
            if (currentUser) {
              // Extract user attributes
              const attributes = currentUser.UserAttributes || [];
              const email = attributes.find(attr => attr.Name === 'email')?.Value || '';
              const firstName = attributes.find(attr => attr.Name === 'given_name')?.Value || '';
              const lastName = attributes.find(attr => attr.Name === 'family_name')?.Value || '';
              const role = attributes.find(attr => attr.Name === 'custom:role')?.Value || 'viewer';
              const department = attributes.find(attr => attr.Name === 'custom:department')?.Value || '';
              
              const user: User = {
                id: currentUser.Username || '',
                email,
                firstName,
                lastName,
                role: role as User['role'],
                department,
                isActive: true,
              };

              setAuthState({
                isAuthenticated: true,
                isLoading: false,
                user,
                token: accessToken,
                error: null,
              });
              return;
            }
          } catch {
            // Token is invalid, remove it
            localStorage.removeItem('authToken');
          }
        }
      }
      
      // Not authenticated
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: null,
      });
    } catch {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: null,
      });
    }
  };

  const handleSignIn = async (email: string, password: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      if (useMockAuth) {
        // Mock authentication
        const mockUser = mockUsers.find(u => u.email === email && u.password === password);
        
        if (mockUser) {
          const userWithoutPassword = { ...mockUser };
          delete (userWithoutPassword as { password?: string }).password;
          localStorage.setItem('mockAuthUser', JSON.stringify(userWithoutPassword));
          
          // Create a mock JWT token with user information
          const mockToken = `mock-jwt-token:${btoa(JSON.stringify(userWithoutPassword))}`;
          localStorage.setItem('authToken', mockToken);
          
          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user: userWithoutPassword,
            token: mockToken,
            error: null,
          });
        } else {
          setAuthState(prev => ({
            ...prev,
            isLoading: false,
            error: 'Invalid email or password',
          }));
          throw new Error('Invalid email or password');
        }
      } else {
        const initiateAuthCommand = new InitiateAuthCommand({
          ClientId: clientId,
          AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
          AuthParameters: {
            USERNAME: email,
            PASSWORD: password,
          },
        });
        
        const result = await cognitoClient.send(initiateAuthCommand);
        
        if (result.AuthenticationResult?.AccessToken) {
          // Store the access token
          localStorage.setItem('authToken', result.AuthenticationResult.AccessToken);
          
          // Get user details and update state
          await checkAuthState();
        } else {
          throw new Error('Authentication failed');
        }
      }
    } catch (error: unknown) {
      if (!useMockAuth) {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Sign in failed',
        }));
      }
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      if (useMockAuth) {
        // Mock sign out
        localStorage.removeItem('mockAuthUser');
        localStorage.removeItem('authToken');
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          token: null,
          error: null,
        });
      } else {
        const accessToken = localStorage.getItem('authToken');
        
        if (accessToken && !accessToken.startsWith('mock-jwt-token')) {
          try {
            const globalSignOutCommand = new GlobalSignOutCommand({
              AccessToken: accessToken,
            });
            
            await cognitoClient.send(globalSignOutCommand);
          } catch {
            // Continue with local sign out even if global sign out fails
          }
        }
        
        localStorage.removeItem('authToken');
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          token: null,
          error: null,
        });
      }
    } catch (error: unknown) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Sign out failed',
      }));
    }
  };

  const handleSignUp = async (email: string, password: string, attributes: SignUpAttributes) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const signUpCommand = new SignUpCommand({
        ClientId: clientId,
        Username: email,
        Password: password,
        UserAttributes: [
          { Name: 'email', Value: email },
          { Name: 'given_name', Value: attributes.given_name || '' },
          { Name: 'family_name', Value: attributes.family_name || '' },
        ],
      });

      await cognitoClient.send(signUpCommand);
      setAuthState(prev => ({ ...prev, isLoading: false }));
    } catch (error: unknown) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Sign up failed',
      }));
      throw error;
    }
  };

  const handleConfirmSignUp = async (email: string, code: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const confirmSignUpCommand = new ConfirmSignUpCommand({
        ClientId: clientId,
        Username: email,
        ConfirmationCode: code,
      });

      await cognitoClient.send(confirmSignUpCommand);
      setAuthState(prev => ({ ...prev, isLoading: false }));
    } catch (error: unknown) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Confirmation failed',
      }));
      throw error;
    }
  };

  const handleResendConfirmationCode = async (email: string) => {
    try {
      const resendCodeCommand = new ResendConfirmationCodeCommand({
        ClientId: clientId,
        Username: email,
      });

      await cognitoClient.send(resendCodeCommand);
    } catch (error: unknown) {
      setAuthState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to resend code',
      }));
      throw error;
    }
  };

  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null }));
  };

  const contextValue: AuthContextType = {
    ...authState,
    signIn: handleSignIn,
    signOut: handleSignOut,
    signUp: handleSignUp,
    confirmSignUp: handleConfirmSignUp,
    resendConfirmationCode: handleResendConfirmationCode,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
